import { Component, Input, computed, effect, signal, Output, EventEmitter } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { FormWidget, FormField, Theme, ColorSet } from '../../models/reader-document';
import { ElementRef, ViewChild } from '@angular/core';
import { ensureGoogleMapsLoaded } from '../../utils/google-maps-loader';
import { DecimalPipe } from '@angular/common';
import { DEFAULT_PADDING } from '../../utils/widget.constants';
import { resolveWidgetStyleColors } from '../../utils/widget-style.utils';
import { ValidationErrors, ValidatorFn } from '../../../../../../node_modules/@angular/forms/types/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked, type Tokens } from 'marked';

type DarkTheme = Theme & { isDark?: boolean };
type MapValue = { lat: number; lng: number } | null;

@Component({
  selector: 'wr-form-widget',
  standalone: true,
  imports: [ReactiveFormsModule, DecimalPipe],
  templateUrl: './form.component.html',
})
export class FormWidgetComponent {

  private widgetSig = signal<FormWidget | null>(null);
  private themeSig = signal<DarkTheme | null>(null);
  //private successTimeout: any = null;

  form = signal<FormGroup | null>(null);
  submitted = signal(false); // validation errors
  success = signal(false);
  submittedOnce = signal(false);    
  private markdownCache = new Map<string, SafeHtml | null>();

  @Output() prepared = new EventEmitter<any>();

  constructor(private fb: FormBuilder,
    private sanitizer: DomSanitizer) {
    effect(() => {
      const w = this.widgetSig();
      if (!w) return;

      // Reset form
      this.submitted.set(false);
      this.success.set(false);
      this.submittedOnce.set(false);  

      const group: Record<string, any> = {};

      for (const section of w.sections ?? []) {
        for (const field of section.fields ?? []) {
          group[this.ctrlName(section.id, field)] = this.createControl(field);
        }
      }

      this.form.set(this.fb.group(group));
    });
  }

  @Input() set json(value: FormWidget) {
    this.markdownCache.clear();
    this.widgetSig.set(value);
  }

  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set((value as DarkTheme) ?? null);
  }

  widget = computed(() => this.widgetSig());
  fg = computed(() => this.form());
  isDark = computed(() => !!this.themeSig()?.isDark);

  private colors = computed(() => {
    const t = this.themeSig();
    if (!t) return null;
    return t.isDark ? t.darkColors : t.lightColors;
  });

  titleColor = computed(() => this.mergedColors().title);
  subtitleColor = computed(() => this.mergedColors().subtitle);
  textColor = computed(() => this.mergedColors().primary);
  backgroundColor = computed(() => this.mergedColors().background);
  accent = computed(() => this.mergedColors().accent);
  cornerRadius = computed(() => this.themeSig()?.cornerRadius ?? 12);
  titleFont = computed(() => this.themeSig()?.fonts?.title?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  subtitleFont = computed(() => this.themeSig()?.fonts?.subtitle?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  plainFont = computed(() => this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  italicFont = computed(() => this.themeSig()?.fonts?.italic?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  boldFont = computed(() => this.themeSig()?.fonts?.bold?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  paddingTop = computed(
    () => this.widget()?.widgetStyle?.padding?.top ?? DEFAULT_PADDING.top
  );
  paddingBottom = computed(
    () => this.widget()?.widgetStyle?.padding?.bottom ?? DEFAULT_PADDING.bottom
  );
  textAlign = computed(() => {
    const align = this.widget()?.widgetStyle?.['contentAlignment'];
    if (align === 'leading') return 'left';
    if (align === 'center') return 'center';
    if (align === 'trailing') return 'right';
    return 'left'; // default
  });

  getMarkdownFromField(field: FormField):SafeHtml | null {
    if(!field) return null
    let text = field.value ?? '';
    if( field.mandatory) {
      text += '*'
    }
    text = text.replace(/^[ \t]*([*-]|\d+\.)[ \t]*\n/gm, '');
    const cacheKey = `${field.id ?? field.key ?? field.title ?? ''}:${text}`;
    if (this.markdownCache.has(cacheKey)) return this.markdownCache.get(cacheKey) ?? null;

    const rawHtml = marked.parse(text, { async: false }) as string;
    const safeHtml = this.sanitizer.bypassSecurityTrustHtml(rawHtml);
    this.markdownCache.set(cacheKey, safeHtml);

    return safeHtml;
  }

  ctrlName(sectionId: string, field: FormField): string {
    return `${sectionId}__${field.id}`;
  }

  private createControl(field: FormField) {
    const validators:any = [];

    if (field.mandatory) {
      if (field.type === 'boolean') {
        validators.push(Validators.requiredTrue);
      } else {
        validators.push(Validators.required);
      }
    }

    if (field.validations){
      field.validations.forEach( validation => {

        try{
          if( validation.type === 'numericRange' && validation?.decimal === false){
            validators.push(Validators.pattern(/^-?\d+$/))
          }
          if (validation.regex){
            validators.push(Validators.pattern(validation.regex));
          }
          if (validation.min && validation.type === "numericRange"){
            validators.push(Validators.min(validation.min));
          }
          if (validation.max && validation.type === "numericRange"){
            validators.push(Validators.max(validation.max));
          }

          if(validation.type === "dateRange") {
            if (validation.min && validation.max){
              validators.push(this.dateRangeValidator(validation.min, validation.max));
            }
            if (!validation.min && validation.max){
              validators.push(this.dateRangeValidator(undefined ,validation.max));
            }
            if (validation.min && !validation.max){
              validators.push(this.dateRangeValidator(validation.min, undefined));
            }
          }

          if(validation.type === "textLength") {
            if (validation.min){
              validators.push(Validators.minLength(validation.min));
            }

            if (validation.max){
              validators.push(Validators.maxLength(validation.max));
            }
          }

          if (validation.minDaysAgo){
            validators.push(this.minDaysAgoValidator(validation.minDaysAgo))
          }
        }catch(e){
          console.log(e)
        }
      })
    }

    switch (field.type) {
      case 'boolean':
        return this.fb.control(false, validators);
      default:
        return this.fb.control('', validators);
    }
  }

  minDaysAgoValidator(daysAgo: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value)
        return null;
  
      const inputDate = new Date(control.value);
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() - daysAgo);
      return (inputDate <= maxDate) ? null : { minDaysAgo: true };
    };
  }

  dateRangeValidator( min?: any, max?: any ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const dateRegx = /^\d{4}-\d{2}-\d{2}$/;// yyyy-mm-dd
      const dateTimeRegx = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;// yyyy-mm-ddTHH:mm
      const timeRegx = /^\d{2}:\d{2}$/;// HH:mm
  
      let minTime:any = min ? new Date(this.convertTimestampToDateTime(min)) : undefined
      let maxTime:any = max ? new Date(this.convertTimestampToDateTime(max)) : undefined
      let selectedDate = timeRegx.test(control.value) ? control.value : new Date(control.value)
  
      if (timeRegx.test(control.value)) {
        minTime = minTime ? minTime.toLocaleTimeString('de-DE', { hour: "2-digit", minute: "2-digit" }) : minTime
        maxTime = maxTime ? maxTime.toLocaleTimeString('de-DE', { hour: "2-digit", minute: "2-digit" }) : maxTime
      } else if (dateRegx.test(control.value)) {
        // minTime = minTime ? minTime.toLocaleDateString('de-DE',{year: 'numeric', month: '2-digit', day:'2-digit'}) : minTime
        // maxTime = maxTime ? maxTime.toLocaleDateString('de-DE',{year: 'numeric', month: '2-digit', day:'2-digit'}) : maxTime
        
        minTime = minTime 
        ? ( () => {
          minTime.setHours(0,0,0,0)
          return minTime.getTime()
        })() : minTime

        maxTime = maxTime 
        ? ( () => {
          maxTime.setHours(0,0,0,0)
          return maxTime.getTime()
        })() : maxTime

        selectedDate = selectedDate 
        ? ( () => {
          selectedDate.setHours(0,0,0,0)
          return new Date(selectedDate).getTime()
        })() : selectedDate

      } else{
        minTime = minTime?.getTime()
        maxTime = maxTime?.getTime()
        selectedDate = selectedDate?.getTime()
      }

      if (minTime && (selectedDate < minTime)) {
        console.log('---mintime---', minTime)
        return {
          minDate: {
            requiredMin: minTime,
            actual: selectedDate
          }
        };
      }
  
      if (maxTime && (selectedDate > maxTime)) {
        console.log('---maxtime---', maxTime)
        return {
          maxDate: {
            requiredMax: maxTime,
            actual: selectedDate
          }
        };
      }
  
      return null;
    };
  }

  private mergedColors = computed(() => {
    const w = this.widget();
    const t = this.themeSig();
    const isDark = t?.isDark ?? false;

    const themeColors: ColorSet = t ? t[isDark ? 'darkColors' : 'lightColors'] : {
      accent: '#ff5e38',
      background: '#f9fafb',
      title: '#111827',
      subtitle: '#4b5563',
      primary: '#111827',
      secondary: '#4b5563',
    };

    // override
    const widgetColors: Partial<ColorSet> = resolveWidgetStyleColors(w?.widgetStyle, isDark);

    return {
      background: widgetColors.background ?? themeColors.background,
      title: widgetColors.title ?? themeColors.title,
      subtitle: widgetColors.subtitle ?? themeColors.subtitle,
      primary: widgetColors.primary ?? themeColors.primary,
      accent: widgetColors.accent ?? themeColors.accent,
    } as ColorSet;
  });

  fieldOptions(field: any): any[] {
    const opts = (field as any).options as any[] | undefined;
    return opts ?? [];
  }

  optionValue(opt: any): string {
    return typeof opt === 'string' ? opt : opt.value;
  }

  optionTitle(opt: any): string {
    return typeof opt === 'string' ? opt : opt.title;
  }

  trackOption(opt: any): string {
    if (typeof opt === 'string') return opt;
    return opt.id ?? opt.value;
  }

  // UI helpers
  control(sectionId: string, field: FormField): AbstractControl | null {
    return this.fg()?.get(this.ctrlName(sectionId, field)) ?? null;
  }

  private validationText(
    key: 'required' | 'format' | 'minimum' | 'maximum' | 'minCharacters' | 'maxCharacters',
    value?: string | number,
  ): string {
    const isGerman = (navigator.language || 'en').toLowerCase().startsWith('de');

    if (isGerman) {
      switch (key) {
        case 'required': return 'Dieses Feld ist erforderlich';
        case 'format': return 'Die Eingabe entspricht nicht dem erforderlichen Format';
        case 'minimum': return `Der Mindestwert ist ${value}`;
        case 'maximum': return `Der Höchstwert ist ${value}`;
        case 'minCharacters': return `Mindestens ${value} Zeichen erforderlich`;
        case 'maxCharacters': return `Maximal ${value} Zeichen erlaubt`;
      }
    }

    switch (key) {
      case 'required': return 'This field is required';
      case 'format': return 'Input does not match the required format';
      case 'minimum': return `Minimum value is ${value}`;
      case 'maximum': return `Maximum value is ${value}`;
      case 'minCharacters': return `Minimum ${value} characters required`;
      case 'maxCharacters': return `Maximum ${value} characters allowed`;
    }

    return '';
  }

  getError(sectionId:string, field: FormField): string[] {
    const errors: string[] = [];
    const control =this.fg()?.get(this.ctrlName(sectionId, field))
    const timeRegx = /^\d{2}:\d{2}$/;// HH:mm
    const locale = navigator.language || 'en';
  
    if (!control || !control.errors || !control.touched) {
      return [];
    }

    if (control.errors['required'] || control.errors['requiredTrue']) {
      errors.push(this.validationText('required'));
    }
  
    if (control.errors['pattern']) {
      console.log('----pattern error---')
      errors.push(this.validationText('format'));
    }
  
    if (control.errors['min']) {
      console.log('----min error---', field)

      if(field.validations?.find(val => val.type === "numericRange"))
      errors.push(this.validationText('minimum', control.errors['min'].min));
    }
  
    if (control.errors['max']) {
      console.log('----max error---')

      if(field.validations?.find(val => val.type === "numericRange"))
        errors.push(this.validationText('maximum', control.errors['max'].max));
    }

    if (control.errors['minDate']){
      if(field.validations?.find(val => val.type === "dateRange")){
        const inputDate = timeRegx.test(control.value) ? control.value : new Date(control.value);
        const reqMin = control.errors['minDate'].requiredMin
        const min = timeRegx.test(reqMin) ? reqMin : this.convertTimestampToDateTime(reqMin)

        let minValue
        if (field.type == "date")
          minValue = new Date(min).toLocaleDateString(locale,{year: 'numeric', month: '2-digit', day:'2-digit'})

        if (field.type == "dateTime")
          minValue = new Date(min).toUTCString()

        if (field.type == "time")
          minValue = timeRegx.test(reqMin) ? reqMin : new Date(min).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })

        const minDate = timeRegx.test(min) ? min : new Date(min);

        errors.push( inputDate < minDate
          ? this.validationText('minimum', minValue)
          : '');
      }
    }

    if (control.errors['maxDate']){
      if(field.validations?.find(val => val.type === "dateRange")){
        const inputDate = timeRegx.test(control.value) ? control.value : new Date(control.value);

        const reqMax = control.errors['maxDate'].requiredMax
        const max = timeRegx.test(reqMax) ? reqMax : this.convertTimestampToDateTime(reqMax)
        let maxValue
        if (field.type == "date")
          maxValue = new Date(max).toLocaleDateString(locale,{year: 'numeric', month: '2-digit', day:'2-digit'})

        if (field.type == "dateTime")
          maxValue = new Date(max).toUTCString()

        if (field.type == "time")
          maxValue = timeRegx.test(reqMax) ? reqMax : new Date(max).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })

        const maxDate = timeRegx.test(max) ? max : new Date(max);

        errors.push( inputDate > maxDate
          ? this.validationText('maximum', maxValue)
          : '');
      }
    }

    if (control.errors['minlength']) {
      console.log('----minlength error---')

      if(field.validations?.find(val => val.type === "textLength"))
        errors.push(this.validationText('minCharacters', control.errors['minlength'].requiredLength));
    }

    if (control.errors['maxlength']) {
      console.log('----maxlength error---')

      if(field.validations?.find(val => val.type === "textLength"))
        errors.push(this.validationText('maxCharacters', control.errors['maxlength'].requiredLength));
    }

    return errors;
  }

  showRequiredError(sectionId: string, field: FormField): boolean {
    const c = this.control(sectionId, field);
    if (!c) return false;
    return (c.touched || this.submitted()) && !!c.errors;
  }

  showError(sectionId: string, field: FormField): boolean {
    const c = this.control(sectionId, field);
    if (!c) return false;
    return (c.touched || this.submitted()) && !!c.errors;
  }

  onSubmit() {
    const w = this.widget();
    const fg = this.fg();
    if (!w || !fg) return;

    this.submitted.set(true);
    fg.markAllAsTouched();

    if (fg.invalid) return;

    const data: Record<string, any> = {};

    for (const section of w.sections ?? []) {
      for (const field of section.fields ?? []) {
        const name = this.ctrlName(section.id, field);
        const raw = fg.get(name)?.value;

        data[field.key] = this.coerceValue(field, raw);
      }
    }

    const payload = {
      widgetId: w.id,
      timeStamp: new Date().toISOString(),
      data,
      workflowTemplateId: (w as any).workflowTemplateId ?? undefined,
      reloadDPP: (w as any).reloadDPP ?? false,
    };

    this.prepared.emit(payload);
    //console.log('Prepared form payload:', payload);

    /*     if (!(w as any).reloadDPP) {
      this.submittedOnce.set(true);
      this.success.set(true);

      fg.reset();
      this.submitted.set(false); 

      if (this.successTimeout) {
        clearTimeout(this.successTimeout);
      }
      this.successTimeout = setTimeout(() => {
        this.success.set(false);
      }, 5000);
    }
    */

    if (!(w as any).reloadDPP) {
      this.submittedOnce.set(true);
      this.success.set(true);

      // clear form values
      fg.reset();
      fg.markAsPristine();
      fg.markAsUntouched();
      this.submitted.set(false);
    }

  }

  startAgain() {
    const w = this.widget();
    const fg = this.fg();
    if (!w || !fg) return;
    // Show again
    this.success.set(false);
    this.submitted.set(false);
    // Clear all values
    fg.reset();
    fg.markAsPristine();
    fg.markAsUntouched();
  }

  private coerceValue(field: FormField, raw: any) {
    if (raw === '' || raw === null || raw === undefined) return raw;

    switch (field.type) {
      case 'number':
        return raw === '' ? null : parseInt(raw, 10);
      case 'decimal':
        return raw === '' ? null : parseFloat(raw);
      case 'boolean':
        // store boolean
        let result:any = !!raw
        if( field.value && !!raw === true)
        result = field.value
        return result;
      case 'map':
        // return {lat,lng}
        return raw;
      default:
        return raw;
    }
  }

  // button style
  submitBtnStyle = computed(() => {
    const w = this.widget();
    const t = this.themeSig();
    const colors = this.colors();
    const s = (w as any)?.submitButtonStyle ?? {};
    return {
      background: s.backgroundColor ?? colors?.accent ?? '#ff5e38',
      border: s.borderColor ?? 'transparent',
      color: s.foregroundColor ?? '#ffffff',
      borderWidth: s.borderWidth ?? 0,
      radius: s.cornerRadius ?? t?.cornerRadius ?? 12,
    };
  });

  hasRequiredFields = computed(() => {
    const widget = this.widget();
    if (!widget?.sections) return false;

    return widget.sections.some(section =>
        section.fields?.some(f => f.mandatory)
    );
  });

  requiredHint = computed(() => {
    const isGerman = (navigator.language || 'en').toLowerCase().startsWith('de');
    return isGerman ? '* = Pflichtfeld' : '* = Required field';
  });

  mapText(key: 'noLocation' | 'choose' | 'select' | 'chooseLocation' | 'cancel'): string {
    const lang = (navigator.language || 'en').toLowerCase();
    const de = lang.startsWith('de');

    switch (key) {
      case 'noLocation':
        return de ? 'Kein Standort ausgewählt' : 'No location selected';
      case 'select':
        return de ? 'Auswählen' : 'Select';
      case 'chooseLocation':
        return de ? 'Standort wählen' : 'Choose location';
      case 'cancel':
        return de ? 'Abbrechen' : 'Cancel';
      default:
        return '';
    }
  }

  // GOOGLE MAPS

  @ViewChild('mapModalContainer') mapModalContainer?: ElementRef<HTMLDivElement>;
  mapModalOpen = signal(false);
  private mapModalField = signal<{ sectionId: string; fieldId: string } | null>(null);
  private mapModalValue = signal<MapValue>(null);
  private mapInstance: any = null;
  private mapMarker: any = null;

  mapValue(sectionId: string, field: any): MapValue {
    const name = this.ctrlName(sectionId, field);
    const form = this.form();
    const raw = form?.get(name)?.value;

    if (!raw) return null;

    if (typeof raw === 'string') {
      const [lat, lng] = raw.split(',').map((x) => parseFloat(x.trim()));
      return isFinite(lat) && isFinite(lng) ? { lat, lng } : null;
    }

    if (typeof raw?.lat === 'number' && typeof raw?.lng === 'number') {
      return raw as MapValue;
    }

    return null;
  }

  private async renderPreviewMap(
    sectionId: string,
    fieldId: string,
    value: MapValue | null
  ) {
    if (!value) return;

    try {
      await ensureGoogleMapsLoaded();
    } catch (err) {
      console.error('Google Maps failed to load for preview', err);
      return;
    }

    const elId = `map-preview-${sectionId}-${fieldId}`;
    const el = document.getElementById(elId) as HTMLDivElement | null;
    if (!el) return; // no preview container (showsMap false or not in DOM)

    const googleAny = (window as any).google;

    const map = new googleAny.maps.Map(el, {
      center: value,
      zoom: 14,
      disableDefaultUI: true,
    });

    new googleAny.maps.Marker({
      position: value,
      map,
    });
  }

  showsMap(field: any) {
    return !!field?.showsMap;
  }

  async openMap(sectionId: string, field: any) {
    this.mapModalField.set({ sectionId, fieldId: field.id });

    this.mapModalValue.set(this.mapValue(sectionId, field));
    this.mapModalOpen.set(true);

    requestAnimationFrame(() => this.initMapWithRetry());
  }

private initMapWithRetry(tries = 0) {
  const host = this.mapModalContainer?.nativeElement;

  if (!host) {
    if (tries < 10) requestAnimationFrame(() => this.initMapWithRetry(tries + 1));
    return;
  }

  this.initMap();
}

  closeMap() {
    this.mapModalOpen.set(false);
    this.mapInstance = null;
    this.mapMarker = null;
  }

  private async initMap() {
    const host = this.mapModalContainer?.nativeElement;
    if (!host) return;

    try {
      await ensureGoogleMapsLoaded();
    } catch (err) {
      console.error('Google Maps failed to load', err);
      return;
    }

    const current = this.mapModalValue();
    const center = current ?? { lat: 47.0, lng: 8.0 };

    const googleAny = (window as any).google;
    this.mapInstance = new googleAny.maps.Map(host, {
      center,
      zoom: current ? 14 : 4,
      disableDefaultUI: true,
    });

    const g = (window as any).google;

    if (g?.maps?.marker?.AdvancedMarkerElement) {
      this.mapMarker = new g.maps.marker.AdvancedMarkerElement({
        map: this.mapInstance,
        position: center,
        gmpDraggable: true,
      });
    } else {
      // fallback for old browsers
      this.mapMarker = new g.maps.Marker({
        map: this.mapInstance,
        position: center,
        draggable: true,
      });
    }

    this.mapInstance.addListener('click', (e: any) => {
      const pos = e.latLng;
      if (!pos) return;
      const value = { lat: pos.lat(), lng: pos.lng() };
      this.mapModalValue.set(value);
      this.mapMarker.setPosition(pos);
    });
  }

  confirmMapSelection() {
    const target = this.mapModalField();
    const value = this.mapModalValue();

    if (!target) {
      this.closeMap();
      return;
    }

    const name = this.ctrlName(target.sectionId, { id: target.fieldId } as any);
    const form = this.form();
    const ctrl = form?.get(name);

    if (ctrl) {
      ctrl.setValue(value);
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      ctrl.updateValueAndValidity();
      this.renderPreviewMap(target.sectionId, target.fieldId, value);
    }

    this.closeMap();
  }

  validateNumberKeys(event: KeyboardEvent) {
    const blockedKeys = ['.', ',', '-', 'e', 'E', '+'];
    if (blockedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }
  validateDecimalKeys(event: KeyboardEvent) {
    const blockedKeys = ['-', 'e', 'E', '+'];
    if (blockedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  isBooleanOn(sectionId: string, field: FormField): boolean {
    const c = this.control(sectionId, field);
    return !!c?.value;
  }

  convertTimestampToDateTime(element:any){
    let dateValue
    dateValue = element

    if(element){
      if(element['_nanoseconds'] || element['_seconds']){
        const NS_TO_MS_MULTIPLIER = 1/1000000
        const SEC_TO_MS_MULTIPLIER = 1000
        const timestampInMilliseconds = element['_seconds'] * SEC_TO_MS_MULTIPLIER + element['_nanoseconds'] * NS_TO_MS_MULTIPLIER
        const date = new Date(timestampInMilliseconds)
        element= date
        dateValue = date.getTime()
      }

      if(element['nanoseconds'] || element['seconds']){
        const NS_TO_MS_MULTIPLIER = 1/1000000
        const SEC_TO_MS_MULTIPLIER = 1000
        const timestampInMilliseconds = element['seconds'] * SEC_TO_MS_MULTIPLIER + element['nanoseconds'] * NS_TO_MS_MULTIPLIER
        const date = new Date(timestampInMilliseconds)
        element= date
        dateValue = date.getTime()
      }

      try{
        const newdate = new Date(element)
        if(newdate)
            dateValue = newdate.getTime()
      }catch(errro){}
    }
   return dateValue
  }



}
